import React from 'react';
import { graphql } from 'gatsby';
import BlockContent from '@sanity/block-content-to-react';
import { GatsbySanityImageFluidNoBase64, RawPortableText } from '../../types';
import serializers from '../../serializers';
import Img from 'gatsby-image/withIEPolyfill';
import clsx from 'clsx';
import { CommonSectionProps, SectionBgColor } from '../../templates/Page';
import { areSameFlatColor } from '../../utils/utils';

import * as styles from './TextAndImageSection.module.scss';

export const TextAndImageSectionFragment = graphql`
  fragment TextAndImageSection on SanityTextAndImageSection {
    title
    _rawText(resolveReferences: { maxDepth: 4 })
    useImage
    imageType
    imageSide
    imagePosition
    image {
      asset {
        fluid(maxWidth: 1680) {
          ...GatsbySanityImageFluid_noBase64
        }
      }
    }
    mobileImage {
      asset {
        fluid(maxWidth: 2800) {
          ...GatsbySanityImageFluid_noBase64
        }
      }
    }
  }
`;

export type TextAndImageSectionProps = {
  title?: string;
  _rawText: RawPortableText;
  useImage?: boolean;
  imageType?: 'rectangular' | 'anchored';
  imageSide?: 'left' | 'right';
  imagePosition?: 'top' | 'bottom';
  image: {
    asset: {
      fluid: GatsbySanityImageFluidNoBase64;
    };
  };
  mobileImage?: {
    asset: {
      fluid: GatsbySanityImageFluidNoBase64;
    };
  };
  className?: string;
};

export function getSectionBgColor(props: TextAndImageSectionProps): SectionBgColor<'white'> {
  return {
    name: 'white',
    type: 'solid',
  };
}

function TextAndImageSection<PrevSectionColorName extends string>(
  props: TextAndImageSectionProps & CommonSectionProps<PrevSectionColorName>,
): React.ReactElement {
  const {
    title,
    _rawText,
    useImage,
    imageType,
    imageSide,
    imagePosition,
    image,
    mobileImage,
    className,
    previousSectionBgColor,
  } = props;

  const anchoredImage = imageType === 'anchored';
  const rectangularImage = imageType === 'rectangular';
  return (
    <section
      id={props.sectionID || ''}
      className={clsx(
        styles.sectionContainer,
        className,
        areSameFlatColor(getSectionBgColor(props), previousSectionBgColor) && styles.noPaddingTop,
      )}
    >
      <div className={clsx(styles.section)}>
        <div
          className={clsx(
            styles.textAndImageContainer,
            !useImage && styles.noImage,
            imageSide === 'left' && styles.imageOnLeft,
            rectangularImage && styles.withRectangularImage,
          )}
        >
          <div className={clsx(styles.textContainer, anchoredImage && styles.withAnchoredImage)}>
            {title && <h2 className={styles.title}>{title}</h2>}
            <BlockContent className={styles.text} blocks={_rawText} serializers={serializers} />
          </div>
          {useImage && image && (
            <Img
              className={clsx(
                styles.image,
                rectangularImage && styles.rectangularImage,
                anchoredImage && styles.anchoredImage,
                !mobileImage && styles.noMobileImage,
                imageSide === 'left' && styles.imageOnLeft,
                anchoredImage && imageSide === 'left' && styles.anchoredOnLeft,
                anchoredImage && imageSide === 'right' && styles.anchoredOnRight,
                anchoredImage && imagePosition === 'top' && styles.anchoredOnTop,
                anchoredImage && imagePosition === 'bottom' && styles.anchoredOnBottom,
              )}
              loading="eager"
              fadeIn={false}
              fluid={image.asset.fluid}
              alt=""
            />
          )}
          {useImage && mobileImage && imageType === 'rectangular' && (
            <Img
              className={styles.mobileImageContainer}
              loading="eager"
              fadeIn={false}
              fluid={mobileImage.asset.fluid}
              alt=""
            />
          )}
        </div>
      </div>
    </section>
  );
}

export default TextAndImageSection;
